import React, { useRef, useEffect } from 'react';
import { api } from './api';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useStoreState } from './store';
import { useStoreActions } from 'easy-peasy';

interface NotificationEntities {
  tender?: string;
  trade?: string;
  order?: string;
}

interface Notification {
  type: string;
  entities: NotificationEntities;
  solution: string;
  label: string;
  subject: string;
}

interface Solution {
  id: string;
  name: string;
}

async function poll(since: Dayjs): Promise<Notification[]> {
  try {
    const response = await api.request({
      url: 'user/notifications',
      headers: {
        'If-Modified-Since': since.locale('en').toString(),
      },
    });
    switch (response.status) {
      case 200: {
        return response.data.notifications;
      }
      default: {
        return [];
      }
    }
  } catch {
    return [];
  }
}

function getUrl(notification: Notification): string | false {
  const { entities } = notification;

  if (entities.tender) return '/tenders';
  if (entities.trade) return `/preview/trade/${entities.trade}`;
  if (entities.order) {
    if (notification.type === 'order_counter') {
      return `/marketplace?order=${entities.order}&tab=counters`;
    }
    return `/marketplace?order=${entities.order}`;
  }

  return false;
}

export function useNotifications(): void {
  const history = useHistory();
  const since = useRef(dayjs.utc());
  const changeSolution = useStoreActions((state) => state.auth.changeSolution);
  const getSolution = useStoreState((state) => (solutionId: string) => {
    const solutions = state.auth.solutions;
    const solutionsById: Record<string, Solution> = {};
    solutions.forEach((sol: Solution) => {
      solutionsById[sol.id] = sol;
    });
    return solutionsById[solutionId];
  });

  useEffect(() => {
    const id = setInterval(async () => {
      const now = dayjs.utc();
      const notifs = await poll(since.current);
      since.current = now;
      for (const n of notifs) {
        if (n.entities) {
          const url = getUrl(n);
          toast(
            React.createElement(
              React.Fragment,
              null,
              React.createElement(
                'h3',
                null,
                `${getSolution(n.solution)?.name}—${n.label}`
              ),
              React.createElement('p', null, n.subject)
            ),
            {
              onClick: () => {
                changeSolution(n.solution);
                if (url) history.push(url);
              },
              pauseOnFocusLoss: true,
              pauseOnHover: true,
            }
          );
        }
      }
    }, 30e3);

    return () => {
      clearInterval(id);
    };
  }, []);
}
